import { useCallback, useEffect, useState } from "react";
import { Eraser, History, Loader2 } from "lucide-react";
import { adminFetch } from "../../lib/adminApi";

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

// "Start fresh": stamps a reporting baseline so every admin report ignores the
// test runs that came before it. Nothing is deleted — customers keep their
// downloads; the owner just stops seeing test noise in the numbers.
export function ReportingBaselineCard({ isSuper }) {
  const [state, setState] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    adminFetch("/api/admin/settings/baseline").then(setState).catch((e) => setErr(e.message));
  }, []);
  useEffect(load, [load]);

  const run = async (method, body) => {
    setBusy(true); setErr("");
    try {
      setState(await adminFetch("/api/admin/settings/baseline", { method, body: body && JSON.stringify(body) }));
      setConfirming(false); setTyped("");
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const hidden = state?.hidden || {};
  const hiddenSummary = [
    [hidden.orders, "orders"], [hidden.pageviews, "page views"], [hidden.leads, "leads"],
    [hidden.previews, "previews"], [hidden.emails, "emails"],
  ].filter(([n]) => n > 0).map(([n, l]) => `${n} ${l}`).join(" · ");

  return (
    <div data-testid="reporting-baseline-card" className="bg-[#12141D] border border-[#262a3d] rounded-2xl p-5">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">Reporting data</p>
      {!state && !err && <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      {state && (
        <>
          {state.baseline ? (
            <p data-testid="baseline-status" className="text-sm text-slate-300">
              Reports start from <span className="text-gold font-mono">{fmtTs(state.baseline)}</span>.
              {hiddenSummary && <span className="text-slate-500"> Hidden test data: {hiddenSummary}.</span>}
            </p>
          ) : (
            <p data-testid="baseline-status" className="text-sm text-slate-300">
              Reports include <span className="text-gold">all history</span> — test runs show up alongside real customers.
            </p>
          )}
          <p className="text-xs text-slate-600 mt-1">
            Starting fresh hides everything recorded before this moment from the Overview, CRM and Conversations.
            Nothing is deleted and customers keep their downloads.
          </p>

          {isSuper && !confirming && (
            <div className="flex gap-2 mt-4 flex-wrap">
              <button data-testid="baseline-start-fresh" onClick={() => setConfirming(true)} disabled={busy}
                      className="bg-gradient-to-r from-gold to-[#FB8500] text-black font-extrabold px-5 py-2.5 rounded-xl text-sm hover:scale-[1.01] transition-transform cursor-pointer flex items-center gap-2 disabled:opacity-50">
                <Eraser className="h-4 w-4" /> START FRESH FROM NOW
              </button>
              {state.baseline && (
                <button data-testid="baseline-show-all" onClick={() => run("DELETE")} disabled={busy}
                        className="border border-[#262a3d] text-slate-400 hover:text-white px-5 py-2.5 rounded-xl text-sm font-bold transition-colors cursor-pointer flex items-center gap-2 disabled:opacity-50">
                  <History className="h-4 w-4" /> Show all history
                </button>
              )}
            </div>
          )}

          {isSuper && confirming && (
            <div data-testid="baseline-confirm" className="mt-4 border border-coral/40 bg-coral/5 rounded-xl p-4">
              <p className="text-sm text-slate-200">
                Everything before <span className="font-mono text-coral">right now</span> will disappear from the reports.
                Type <span className="font-mono text-white">RESET</span> to confirm.
              </p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <input data-testid="baseline-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)}
                       placeholder="RESET" autoFocus
                       className="bg-obsidian border border-[#262a3d] rounded-xl px-4 py-2.5 text-sm font-mono outline-none focus:border-coral/60 w-36" />
                <button data-testid="baseline-confirm-submit" onClick={() => run("POST", { confirm: "RESET" })}
                        disabled={busy || typed !== "RESET"}
                        className="bg-coral text-black font-extrabold px-5 py-2.5 rounded-xl text-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed">
                  {busy ? "Working…" : "Yes, start fresh"}
                </button>
                <button data-testid="baseline-confirm-cancel" onClick={() => { setConfirming(false); setTyped(""); }}
                        className="text-slate-400 hover:text-white text-sm px-3 cursor-pointer transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
          {!isSuper && <p className="text-xs text-slate-600 mt-3">Only super admins can change the reporting baseline.</p>}
        </>
      )}
      {err && <p data-testid="baseline-error" className="text-coral text-sm mt-3">{err}</p>}
    </div>
  );
}
